import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";

import type {
  DailyClientDebtDetail,
  DailyDebtSummary,
  SortColumn,
  SortDirection,
} from "../types";

interface UseDailyDebtSnapshotParams {
  date: string | null;
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  sortColumn?: SortColumn;
  sortDirection?: SortDirection;
}

interface DebtSummaryData {
  summary: DailyDebtSummary | null;
  previousDayDebt: number | null;
}

interface ClientDebtData {
  clients: DailyClientDebtDetail[];
  totalClients: number;
}

export const useDailyDebtSnapshot = ({
  date,
  page = 1,
  pageSize = 50,
  searchTerm = "",
  sortColumn = "debt_amount",
  sortDirection = "desc",
}: UseDailyDebtSnapshotParams) => {
  const summaryQuery = useQuery<DebtSummaryData>({
    queryKey: ["daily-debt-summary", date],
    queryFn: async () => {
      if (!date) return { summary: null, previousDayDebt: null };

      const { data: summaryData, error: summaryError } = await supabase
        .from("daily_debt_summary")
        .select("*")
        .eq("snapshot_date", date)
        .single();

      if (summaryError) throw summaryError;

      const { data: prevData } = await supabase
        .from("daily_debt_summary")
        .select("total_debt")
        .lt("snapshot_date", date)
        .order("snapshot_date", { ascending: false })
        .limit(1)
        .single();

      return {
        summary: summaryData as DailyDebtSummary,
        previousDayDebt: prevData?.total_debt ?? null,
      };
    },
    enabled: !!date,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });

  const clientsQuery = useQuery<ClientDebtData>({
    queryKey: [
      "daily-client-debt-details",
      date,
      page,
      pageSize,
      searchTerm,
      sortColumn,
      sortDirection,
    ],
    queryFn: async () => {
      if (!date) return { clients: [], totalClients: 0 };

      let query = supabase
        .from("daily_client_debt_details")
        .select("*", { count: "exact" })
        .eq("snapshot_date", date);

      if (searchTerm) {
        query = query.or(
          `client_name.ilike.%${searchTerm}%,client_rut.ilike.%${searchTerm}%`
        );
      }

      query = query.order(sortColumn, { ascending: sortDirection === "asc" });

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data: clientsData, count, error: clientsError } = await query;

      if (clientsError) throw clientsError;

      return {
        clients: (clientsData || []) as DailyClientDebtDetail[],
        totalClients: count || 0,
      };
    },
    enabled: !!date,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });

  const totalClients = clientsQuery.data?.totalClients || 0;

  return {
    summary: summaryQuery.data?.summary || null,
    previousDayDebt: summaryQuery.data?.previousDayDebt || null,
    clients: clientsQuery.data?.clients || [],
    totalClients,
    totalPages: Math.ceil(totalClients / pageSize),
    loading: summaryQuery.isLoading || clientsQuery.isLoading,
    error: summaryQuery.error?.message || clientsQuery.error?.message || null,
  };
};
